/**
 * TrajectoryTree — the family-tree view of the task's rollouts. Runs
 * share a node while their step outcomes match and fork into child
 * branches at the first step where they differ. Nodes stack top-down
 * with hairline connectors (parent stub, sibling bar, child stub);
 * shared steps render as outcome squares with hover popovers; leaves
 * carry the run chips that open transcripts.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { OutcomeLegend } from "@/components/benchmarks/OutcomeLegend";
import {
  buildTrajectoryTree,
  humanizeTag,
  outcomeFill,
  PASS_BAR,
  whatHappenedAt,
  type TrajectoryTreeNode, processEntryFor } from "@/components/benchmarks/goldenProcess";
import { formatScore } from "@/components/evals/format";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type {
  GoldenStep,
  ProcessRollout,
  RolloutEntry,
  TaskProcessResponse,
} from "@/api/generated/model";

interface TreeProps {
  entries: RolloutEntry[];
  selected: { model: string; rolloutId: string } | undefined;
  onSelect: (entry: RolloutEntry) => void;
}

/** Where the subtree is headed, judged against the tint bar. */
type SubtreeStatus = "pass" | "fail" | "mixed";

function subtreeStatus(rollouts: ProcessRollout[]): SubtreeStatus {
  const scored = rollouts.filter(
    (r): r is ProcessRollout & { score: number } => r.score !== null,
  );
  if (scored.length === 0) return "mixed";
  if (scored.length === rollouts.length) {
    if (scored.every((r) => r.score >= PASS_BAR)) return "pass";
    if (scored.every((r) => r.score < PASS_BAR)) return "fail";
  }
  return "mixed";
}

const STATUS_ACCENT: Record<SubtreeStatus, string> = {
  pass: "border-l-success",
  fail: "border-l-destructive",
  mixed: "border-l-brand-neutral-olive",
};

const STATUS_EDGE: Record<SubtreeStatus, string> = {
  pass: "border-success",
  fail: "border-destructive",
  mixed: "border-brand-neutral-olive/60",
};

function passCount(rollouts: ProcessRollout[]): number {
  return rollouts.filter((r) => r.score !== null && r.score >= PASS_BAR).length;
}

function NodeBox({
  node,
  stepNumberOf,
  forkStep,
  entries,
  selected,
  onSelect,
}: TreeProps & {
  node: TrajectoryTreeNode;
  stepNumberOf: (stepId: string) => number;
  /** The parent's fork step: what this box's outcome was scored on. */
  forkStep: GoldenStep | undefined;
}): React.JSX.Element {
  const isLeaf = node.branchStep === null;
  const status = subtreeStatus(node.rollouts);
  // Pass/fail and the rate cover SCORED runs only. An unscored run is
  // undecided (same stance as subtreeStatus), so it gets its own count
  // instead of quietly landing in the F bucket.
  const total = node.rollouts.length;
  const scored = node.rollouts.filter((r) => r.score !== null).length;
  const passed = passCount(node.rollouts);
  const unscored = total - scored;
  const passRate = scored > 0 ? passed / scored : null;
  const story =
    forkStep !== undefined ? whatHappenedAt(node, forkStep.id) : null;
  return (
    <div
      className={cn(
        "w-56 rounded-md border border-border border-l-4 bg-card p-2",
        STATUS_ACCENT[status],
      )}
    >
      {node.outcome !== null && forkStep !== undefined ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <div>
              <p className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="size-2.5 flex-none rounded"
                  style={{ backgroundColor: outcomeFill(node.outcome) }}
                />
                <span className="text-xs font-medium text-foreground">
                  {humanizeTag(node.outcome)}
                </span>
                <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                  step {stepNumberOf(forkStep.id)}
                </span>
              </p>
              {story !== null && (
                <p className="mt-1 line-clamp-2 text-xs leading-4 text-muted-foreground">
                  {story}
                </p>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-96">
            <p className="text-xs text-foreground">
              {stepNumberOf(forkStep.id)}. {forkStep.title}
            </p>
            <p className="font-mono text-xs text-muted-foreground">
              {humanizeTag(node.outcome)}
            </p>
            {story !== null && (
              <p className="mt-1 text-xs text-muted-foreground">{story}</p>
            )}
          </TooltipContent>
        </Tooltip>
      ) : (
        <p className="text-xs font-medium text-foreground">All runs</p>
      )}
      <p className="mt-1.5 font-mono text-[10px] tabular-nums text-muted-foreground">
        P {passed} F {scored - passed}
        {unscored > 0 && ` · ${unscored.toString()} unscored`} · {total} total
        {passRate !== null && ` · ${formatScore(passRate)}`}
      </p>
      {passRate !== null && (
        <div
          aria-hidden="true"
          className="mt-1 h-1 overflow-hidden rounded-full bg-muted"
        >
          <span
            className="block h-full bg-success"
            style={{ width: `${(passRate * 100).toString()}%` }}
          />
        </div>
      )}
      {isLeaf && (
        <div className="mt-1.5 space-y-1">
          {node.rollouts.map((rollout) => {
            const entry = processEntryFor(rollout, entries);
            const isSelected =
              selected !== undefined &&
              selected.model === rollout.model &&
              selected.rolloutId === rollout.rollout_id;
            const label = `${rollout.model} · ${
              entry !== undefined ? rollout.rollout_id : rollout.label
            } · ${formatScore(rollout.score)}`;
            return (
              <Tooltip
                key={`${rollout.model}/${rollout.label}`}
                delayDuration={0}
              >
                <TooltipTrigger asChild>
                  {entry !== undefined ? (
                    <button
                      type="button"
                      onClick={() => {
                        onSelect(entry);
                      }}
                      className={cn(
                        "block w-full truncate rounded border border-border px-1.5 py-0.5 text-left font-mono text-[10px] text-foreground hover:bg-muted/50",
                        isSelected && "bg-muted",
                      )}
                    >
                      {label}
                    </button>
                  ) : (
                    <span className="block w-full truncate rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                      {label}
                    </span>
                  )}
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-80">
                  <p className="font-mono text-xs text-foreground">{label}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TreeBranch({
  node,
  stepNumberOf,
  forkStep,
  ...treeProps
}: TreeProps & {
  node: TrajectoryTreeNode;
  stepNumberOf: (stepId: string) => number;
  forkStep: GoldenStep | undefined;
}): React.JSX.Element {
  return (
    <div className="flex flex-col items-center">
      <div className="px-2">
        <NodeBox
          node={node}
          stepNumberOf={stepNumberOf}
          forkStep={forkStep}
          {...treeProps}
        />
      </div>
      {node.branchStep !== null && (
        <>
          <span aria-hidden="true" className="h-4 w-px bg-border" />
          <div className="flex items-start">
            {node.children.map((child, index) => {
              const isFirst = index === 0;
              const isLast = index === node.children.length - 1;
              const edge = STATUS_EDGE[subtreeStatus(child.rollouts)];
              return (
                <div
                  key={child.outcome ?? "root"}
                  className="flex flex-col items-center"
                >
                  {/* Connector row: the sibling bar (top borders on the
                      inner halves) plus this child's center stub, colored
                      by where the child's subtree is headed. */}
                  <div aria-hidden="true" className="flex h-4 w-full">
                    <span
                      className={cn(
                        "h-full w-1/2",
                        !isFirst && "border-t border-border",
                      )}
                    />
                    <span
                      className={cn(
                        "h-full w-1/2 border-l",
                        edge,
                        !isLast && "border-t border-t-border",
                      )}
                    />
                  </div>
                  <TreeBranch
                    node={child}
                    stepNumberOf={stepNumberOf}
                    forkStep={node.branchStep ?? undefined}
                    {...treeProps}
                  />
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

interface ViewState {
  x: number;
  y: number;
  scale: number;
}

const MIN_SCALE = 0.25;
const MAX_SCALE = 2.5;

/**
 * Drag-to-pan, wheel-to-zoom viewport (CSS transform, no dependency).
 * Zoom centers on the cursor; a drag past a small threshold suppresses
 * the click that would otherwise fire on the chip under the pointer.
 * The +/- and reset buttons are the keyboard path to the same view.
 */
function PanZoom({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<ViewState>({ x: 0, y: 0, scale: 1 });
  const drag = useRef<{
    startX: number;
    startY: number;
    moved: boolean;
  } | null>(null);

  // Start with the tree centered horizontally in the viewport.
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (viewport === null || content === null) return;
    setView({
      x: (viewport.clientWidth - content.scrollWidth) / 2,
      y: 8,
      scale: 1,
    });
  }, []);

  // React registers wheel listeners passively, so zoom needs a native
  // non-passive listener to keep the page from scrolling underneath.
  useEffect(() => {
    const viewport = viewportRef.current;
    if (viewport === null) return;
    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      const rect = viewport.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const py = event.clientY - rect.top;
      setView((current) => {
        const scale = Math.min(
          Math.max(current.scale * Math.exp(-event.deltaY * 0.0015), MIN_SCALE),
          MAX_SCALE,
        );
        const ratio = scale / current.scale;
        return {
          scale,
          x: px - (px - current.x) * ratio,
          y: py - (py - current.y) * ratio,
        };
      });
    };
    viewport.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      viewport.removeEventListener("wheel", onWheel);
    };
  }, []);

  const zoomBy = (factor: number): void => {
    const viewport = viewportRef.current;
    if (viewport === null) return;
    const px = viewport.clientWidth / 2;
    const py = viewport.clientHeight / 2;
    setView((current) => {
      const scale = Math.min(
        Math.max(current.scale * factor, MIN_SCALE),
        MAX_SCALE,
      );
      const ratio = scale / current.scale;
      return {
        scale,
        x: px - (px - current.x) * ratio,
        y: py - (py - current.y) * ratio,
      };
    });
  };

  const reset = (): void => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (viewport === null || content === null) return;
    setView({
      x: (viewport.clientWidth - content.scrollWidth) / 2,
      y: 8,
      scale: 1,
    });
  };

  return (
    <div
      ref={viewportRef}
      className="relative h-[70vh] cursor-grab select-none overflow-hidden rounded-md border border-border active:cursor-grabbing"
      style={{
        backgroundImage:
          "linear-gradient(hsl(var(--border) / 0.4) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border) / 0.4) 1px, transparent 1px)",
        backgroundSize: "24px 24px",
      }}
      onPointerDown={(event) => {
        drag.current = {
          startX: event.clientX,
          startY: event.clientY,
          moved: false,
        };
      }}
      onPointerMove={(event) => {
        const state = drag.current;
        if (state === null) return;
        // A release outside the viewport never reaches onPointerUp, so a
        // stale drag would pan on plain hover. No button held, no drag.
        if (event.buttons === 0) {
          drag.current = null;
          return;
        }
        const dx = event.clientX - state.startX;
        const dy = event.clientY - state.startY;
        if (!state.moved && Math.hypot(dx, dy) < 4) return;
        // Capture only once the drag is real: capturing on pointerdown
        // retargets the whole pointer stream to the viewport, which eats
        // the click on the zoom buttons and rollout chips.
        if (!state.moved) {
          event.currentTarget.setPointerCapture(event.pointerId);
        }
        state.moved = true;
        state.startX = event.clientX;
        state.startY = event.clientY;
        setView((current) => ({
          ...current,
          x: current.x + dx,
          y: current.y + dy,
        }));
      }}
      onPointerUp={() => {
        // Keep `moved` until the click event so the capture handler can
        // swallow a drag-release over a chip.
        if (drag.current !== null && !drag.current.moved) drag.current = null;
      }}
      onClickCapture={(event) => {
        if (drag.current?.moved === true) {
          event.preventDefault();
          event.stopPropagation();
        }
        drag.current = null;
      }}
    >
      <div
        ref={contentRef}
        className="inline-block origin-top-left"
        style={{
          transform: `translate(${view.x.toString()}px, ${view.y.toString()}px) scale(${view.scale.toString()})`,
        }}
      >
        {children}
      </div>
      <div className="absolute right-2 top-2 flex gap-1">
        <button
          type="button"
          aria-label="Zoom in"
          onClick={() => {
            zoomBy(1.25);
          }}
          className="rounded border border-border bg-card px-2 py-0.5 font-mono text-xs text-foreground hover:bg-muted/50"
        >
          +
        </button>
        <button
          type="button"
          aria-label="Zoom out"
          onClick={() => {
            zoomBy(0.8);
          }}
          className="rounded border border-border bg-card px-2 py-0.5 font-mono text-xs text-foreground hover:bg-muted/50"
        >
          −
        </button>
        <button
          type="button"
          onClick={reset}
          className="rounded border border-border bg-card px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-foreground hover:bg-muted/50"
        >
          reset
        </button>
      </div>
    </div>
  );
}

export function TrajectoryTree({
  process,
  ...treeProps
}: TreeProps & { process: TaskProcessResponse }): React.JSX.Element {
  const tree = buildTrajectoryTree(process);
  const stepNumberOf = (stepId: string): number =>
    process.steps.findIndex((step) => step.id === stepId) + 1;
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Each level is one golden-process step. Runs share a box while their
        outcomes match and fork where they differ. The left edge is green when
        every run under the box scores at least 90% and red when every run
        scores below that. An olive edge means the runs split. Drag to pan and
        scroll to zoom.
      </p>
      <OutcomeLegend />
      <PanZoom>
        <div className="p-4">
          <TreeBranch
            node={tree}
            stepNumberOf={stepNumberOf}
            forkStep={undefined}
            {...treeProps}
          />
        </div>
      </PanZoom>
    </div>
  );
}
